import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  CertificateTemplateDto,
  CreateCertificateTemplateDto,
  UpdateCertificateTemplateDto,
  CertificateTemplateFilterDto,
  PaginatedCertificateTemplateResponse,
  ApiResponse
} from '@/types/certificate-template.types';
import { CertificateTemplateType, getDefaultAllowedVariables, SYSTEM_TEMPLATE_KEYS } from '@/types/certificate-template.types';

/**
 * Certificate Template Service
 * Handles all API calls related to certificate template management
 */
class CertificateTemplateService {
  /**
   * Get all certificate templates with optional filtering and pagination
   * @param filters - Filter criteria (template_type, is_active, is_default, search, include_deleted, page, limit)
   * @returns Paginated certificate templates response
   */
  async getAllTemplates(filters?: CertificateTemplateFilterDto): Promise<PaginatedCertificateTemplateResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (filters?.template_type) {
        queryParams.append('template_type', filters.template_type);
      }
      if (filters?.is_active !== undefined) {
        queryParams.append('is_active', String(filters.is_active));
      }
      if (filters?.is_default !== undefined) {
        queryParams.append('is_default', String(filters.is_default));
      }
      if (filters?.include_deleted) {
        queryParams.append('include_deleted', String(filters.include_deleted));
      }
      if (filters?.only_deleted) {
        queryParams.append('only_deleted', 'true');
      }
      if (filters?.include_usage) {
        queryParams.append('include_usage', 'true');
      }
      if (filters?.search) {
        queryParams.append('search', filters.search);
      }
      if (filters?.page) {
        queryParams.append('page', String(filters.page));
      }
      if (filters?.limit) {
        queryParams.append('limit', String(filters.limit));
      }

      const url = `${API_ENDPOINTS.CERTIFICATE_TEMPLATES.LIST}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get<ApiResponse<PaginatedCertificateTemplateResponse>>(url);

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch certificate templates');
    } catch (error: any) {
      console.error('Error fetching certificate templates:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch certificate templates';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get certificate template by ID
   * @param id - Template MongoDB ObjectId
   * @returns Certificate template details
   */
  async getTemplateById(id: string): Promise<CertificateTemplateDto> {
    try {
      const response = await apiClient.get<ApiResponse<CertificateTemplateDto>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.GET_BY_ID(id)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch certificate template');
    } catch (error: any) {
      console.error('Error fetching certificate template:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch certificate template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get certificate template by unique key
   * @param key - Template unique key
   * @returns Certificate template details
   */
  async getTemplateByKey(key: string): Promise<CertificateTemplateDto> {
    try {
      const response = await apiClient.get<ApiResponse<CertificateTemplateDto>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.GET_BY_KEY(key)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch certificate template');
    } catch (error: any) {
      console.error('Error fetching certificate template by key:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch certificate template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get certificate template by type (with fallback)
   * @param type - Certificate template type
   * @returns Certificate template or null
   */
  async getTemplateByType(type: CertificateTemplateType): Promise<CertificateTemplateDto | null> {
    try {
      const response = await apiClient.get<ApiResponse<CertificateTemplateDto | null>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.GET_BY_TYPE(type)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch certificate template by type');
    } catch (error: any) {
      console.error('Error fetching certificate template by type:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch certificate template by type';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get default template for a certificate type
   * @param type - Certificate template type
   * @returns Default certificate template or null
   */
  async getDefaultTemplateByType(type: CertificateTemplateType): Promise<CertificateTemplateDto | null> {
    try {
      const response = await apiClient.get<ApiResponse<CertificateTemplateDto | null>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.GET_DEFAULT(type)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch default certificate template');
    } catch (error: any) {
      console.error('Error fetching default certificate template:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch default certificate template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get templates by specific type
   * @param type - Certificate template type
   * @returns List of templates of specified type
   */
  async getTemplatesByType(type: CertificateTemplateType): Promise<CertificateTemplateDto[]> {
    try {
      const response = await apiClient.get<ApiResponse<CertificateTemplateDto[]>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.GET_TEMPLATES_BY_TYPE(type)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to fetch templates by type');
    } catch (error: any) {
      console.error('Error fetching templates by type:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to fetch templates by type';
      throw new Error(errorMessage);
    }
  }

  /**
   * Create a new certificate template
   * @param templateData - Template creation data
   * @returns Created template
   */
  async createTemplate(templateData: CreateCertificateTemplateDto): Promise<CertificateTemplateDto> {
    try {
      const response = await apiClient.post<ApiResponse<CertificateTemplateDto>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.CREATE,
        templateData
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to create certificate template');
    } catch (error: any) {
      console.error('Error creating certificate template:', error);

      // Handle specific error codes (backend sends error_code / error_description)
      const apiError = error?.response?.data?.error;
      const errorCode = apiError?.error_code || apiError?.code;
      if (errorCode === 'duplicate_template_key') {
        throw new Error('Template key or name already exists. Please use a different key/name.');
      }
      if (errorCode === 'reserved_template_key') {
        throw new Error('This template key is reserved for a built-in template. Please use a different key.');
      }

      const errorMessage = apiError?.error_description ||
                          apiError?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to create certificate template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing certificate template
   * @param id - Template ID
   * @param templateData - Template update data (partial)
   * @returns Updated template
   */
  async updateTemplate(id: string, templateData: UpdateCertificateTemplateDto): Promise<CertificateTemplateDto> {
    try {
      const response = await apiClient.put<ApiResponse<CertificateTemplateDto>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.UPDATE(id),
        templateData
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to update certificate template');
    } catch (error: any) {
      console.error('Error updating certificate template:', error);
      // error_description carries the reason (e.g. "A template named 'X' already exists")
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to update certificate template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Set a template as default for its type
   * @param id - Template ID
   * @returns Updated template
   */
  async setAsDefault(id: string): Promise<CertificateTemplateDto> {
    try {
      const response = await apiClient.patch<ApiResponse<CertificateTemplateDto>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.SET_DEFAULT(id)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to set template as default');
    } catch (error: any) {
      console.error('Error setting template as default:', error);
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to set template as default';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a certificate template (soft delete)
   * @param id - Template ID
   * @returns Deletion confirmation
   */
  async deleteTemplate(id: string): Promise<{ success: boolean; code: number }> {
    try {
      const response = await apiClient.delete<ApiResponse<{ success: boolean; code: number }>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.DELETE(id)
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to delete certificate template');
    } catch (error: any) {
      console.error('Error deleting certificate template:', error);
      // error_description carries the "chosen in N course(s)…" reason when delete is blocked
      const errorMessage = error?.response?.data?.error?.error_description ||
                          error?.response?.data?.error?.message ||
                          error?.response?.data?.description ||
                          error?.message ||
                          'Failed to delete certificate template';
      throw new Error(errorMessage);
    }
  }

  /**
   * Restore a deleted template from the Trash. It comes back as a draft (not in use).
   * template_name is only needed when another template took its name meanwhile.
   */
  async restoreTemplate(id: string, template_name?: string): Promise<CertificateTemplateDto> {
    try {
      const response = await apiClient.patch<ApiResponse<CertificateTemplateDto>>(
        API_ENDPOINTS.CERTIFICATE_TEMPLATES.RESTORE(id),
        template_name ? { template_name } : {}
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to restore certificate template');
    } catch (error: any) {
      console.error('Error restoring certificate template:', error);
      const err = new Error(error?.response?.data?.error?.error_description ||
                            error?.response?.data?.error?.message ||
                            error?.message ||
                            'Failed to restore certificate template') as Error & { code?: string };
      err.code = error?.response?.data?.error?.code || error?.response?.data?.error?.error_code;
      throw err;
    }
  }

  /**
   * Replace variables in template text with sample data for preview
   * @param template - Template text with {{variables}}
   * @param sampleData - Object with variable names as keys
   * @returns Template with variables replaced
   */
  replaceVariables(template: string, sampleData: Record<string, any>): string {
    try {
      if (!template) return '';

      let result = template;
      Object.keys(sampleData).forEach(key => {
        try {
          const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`\\{\\{${escapedKey}\\}\\}`, 'g');
          const value = sampleData[key];
          const stringValue = value !== null && value !== undefined ? String(value) : '';
          result = result.replace(regex, stringValue);
        } catch (error) {
          console.error(`Error replacing variable ${key}:`, error);
        }
      });
      return result;
    } catch (error) {
      console.error('Error in replaceVariables:', error);
      return template;
    }
  }

  /**
   * Extract variables from template text
   * @param templateText - Template text containing {{variables}}
   * @returns Array of variable names found
   */
  extractVariablesFromTemplate(templateText: string): string[] {
    const regex = /\{\{([a-z_][a-z0-9_]*)\}\}/gi;
    const matches = templateText.matchAll(regex);
    const variables = new Set<string>();

    for (const match of matches) {
      variables.add(match[1]);
    }

    return Array.from(variables);
  }

  /**
   * Extract variables from canvas layout
   * @param canvasLayout - Canvas layout data with elements
   * @returns Array of variable names found in VARIABLE elements
   */
  extractVariablesFromCanvasLayout(canvasLayout: any): string[] {
    if (!canvasLayout?.elements) return [];

    const variables = new Set<string>();
    for (const element of canvasLayout.elements) {
      if (element.type === 'VARIABLE' && element.variableKey) {
        variables.add(element.variableKey);
      }
    }
    return Array.from(variables);
  }

  /**
   * Serialize canvas layout for storage
   * @param canvasLayout - Canvas layout object
   * @returns JSON string
   */
  serializeCanvasLayout(canvasLayout: any): string {
    try {
      return JSON.stringify(canvasLayout);
    } catch (error) {
      console.error('Error serializing canvas layout:', error);
      return '{}';
    }
  }

  /**
   * Parse canvas layout from storage
   * @param layoutString - JSON string of canvas layout
   * @returns Canvas layout object
   */
  parseCanvasLayout(layoutString: string): any {
    try {
      return JSON.parse(layoutString);
    } catch (error) {
      console.error('Error parsing canvas layout:', error);
      return null;
    }
  }

  /**
   * Duplicate a template
   * @param id - Template ID to duplicate
   * @param newKey - New template key
   * @param newName - New template name
   * @returns Created template
   */
  async duplicateTemplate(
    id: string,
    newKey: string,
    newName: string
  ): Promise<CertificateTemplateDto> {
    try {
      const original = await this.getTemplateById(id);

      const duplicateData: CreateCertificateTemplateDto = {
        template_key: newKey,
        template_name: newName,
        template_type: original.template_type,
        html_template: original.html_template,
        allowed_variables: original.allowed_variables,
        is_active: false, // Duplicates start as inactive
        is_default: false // Duplicates are never default
      };

      return this.createTemplate(duplicateData);
    } catch (error: any) {
      console.error('Error duplicating template:', error);
      throw new Error(error?.message || 'Failed to duplicate template');
    }
  }

  /**
   * Save a brand-new template (called by the editor's first Save, never earlier).
   * Saved as a draft (not active, not default) so issuing is unaffected until an admin picks it.
   */
  async createDraftTemplate(params: {
    template_type: CertificateTemplateType;
    template_name: string;
    html_template: string;
    allowed_variables: string[];
  }): Promise<CertificateTemplateDto> {
    const { template_type, template_name, html_template, allowed_variables } = params;
    return this.createTemplate({
      template_key: await this.makeTemplateKey(template_name, template_type),
      template_name: template_name.trim(),
      template_type,
      html_template,
      allowed_variables: allowed_variables.length ? allowed_variables : getDefaultAllowedVariables(template_type),
      is_active: false,
      is_default: false
    });
  }

  /**
   * Readable key from the name, same style as the built-in ones: "Gold Certificate 2026" → GOLD_CERTIFICATE_2026.
   * Adds _2, _3… when taken. Deleted templates count too (the database keeps their keys unique).
   */
  async makeTemplateKey(template_name: string, template_type: CertificateTemplateType): Promise<string> {
    const base = template_name
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '') // é → e
      .toUpperCase().replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '').slice(0, 60).replace(/_+$/, '')
      || template_type; // e.g. a name written only in Tamil or Arabic letters
    // Built-in keys are reserved even if a tenant is missing one (backend refuses them)
    const taken = new Set<string>(SYSTEM_TEMPLATE_KEYS);
    for (const only_deleted of [false, true]) {
      // Backend allows max 100 per page
      for (let page = 1; ; page++) {
        const result = await this.getAllTemplates({ page, limit: 100, ...(only_deleted ? { only_deleted: true } : {}) });
        (result?.templates || []).forEach(t => t.template_key && taken.add(t.template_key.toUpperCase()));
        if (!result || page >= (result.total_pages || 1)) break;
      }
    }
    if (!taken.has(base)) return base;
    for (let n = 2; ; n++) if (!taken.has(`${base}_${n}`)) return `${base}_${n}`;
  }

  /** Every (not deleted) template of one type, drafts included — for "choose a template" dropdowns */
  async getAllTemplatesOfType(template_type: CertificateTemplateType): Promise<CertificateTemplateDto[]> {
    const all: CertificateTemplateDto[] = [];
    // Backend allows max 100 per page
    for (let page = 1; ; page++) {
      const result = await this.getAllTemplates({ template_type, page, limit: 100 });
      all.push(...(result?.templates || []));
      if (!result || page >= (result.total_pages || 1)) return all;
    }
  }

  /** True when a template (not deleted) already has this exact name — the backend rejects duplicates */
  async isTemplateNameTaken(template_name: string): Promise<boolean> {
    const name = template_name.trim();
    // Backend allows max 100 per page
    for (let page = 1; ; page++) {
      const result = await this.getAllTemplates({ page, limit: 100 });
      if ((result?.templates || []).some(t => t.template_name.trim() === name)) return true;
      if (!result || page >= (result.total_pages || 1)) return false;
    }
  }

  /** Free name to offer when restoring and the old name is taken: "Gold" → "Gold (2)", "Gold (3)"… */
  async suggestFreeTemplateName(template_name: string): Promise<string> {
    const base = template_name.trim().replace(/\s*\(\d+\)$/, '').slice(0, 94);
    const taken = new Set<string>();
    // Backend allows max 100 per page
    for (let page = 1; ; page++) {
      const result = await this.getAllTemplates({ page, limit: 100 });
      (result?.templates || []).forEach(t => taken.add(t.template_name.trim()));
      if (!result || page >= (result.total_pages || 1)) break;
    }
    for (let n = 2; ; n++) if (!taken.has(`${base} (${n})`)) return `${base} (${n})`;
  }

  /**
   * Generate HTML template from canvas layout data
   * Used when saving from the visual canvas builder
   * @param layoutData - Canvas layout data with elements
   * @param sampleData - Optional sample data for preview
   * @returns Generated HTML string
   */
  generateHtmlFromCanvasLayout(layoutData: any, sampleData?: Record<string, string>): string {
    if (!layoutData?.canvas || !layoutData?.elements) {
      return '';
    }

    const { canvas, elements } = layoutData;
    const bgColor = canvas.background?.color || '#FFFFFF';
    const borderStyle = canvas.border
      ? `border: ${canvas.border.width}px ${canvas.border.style} ${canvas.border.color};`
      : '';
    const borderRadius = canvas.border?.radius ? `border-radius: ${canvas.border.radius}px;` : '';

    // Generate element HTML
    const elementsHtml = elements
      .filter((el: any) => el.visible !== false)
      .sort((a: any, b: any) => (a.position?.zIndex || 0) - (b.position?.zIndex || 0))
      .map((el: any) => this.generateElementHtml(el, sampleData))
      .join('\n');

    // Generate complete HTML document
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
    }
    .certificate {
      width: ${canvas.width}px;
      height: ${canvas.height}px;
      position: relative;
      background: ${bgColor};
      ${borderStyle}
      ${borderRadius}
      overflow: hidden;
    }
  </style>
</head>
<body>
  <div class="certificate">
    ${elementsHtml}
  </div>
</body>
</html>`;
  }

  /**
   * Generate HTML for a single canvas element
   * @param element - Canvas element
   * @param sampleData - Optional sample data for variables
   * @returns HTML string for the element
   */
  private generateElementHtml(element: any, sampleData?: Record<string, string>): string {
    const pos = element.position || {};
    const baseStyle = `
      position: absolute;
      left: ${pos.x || 0}px;
      top: ${pos.y || 0}px;
      width: ${pos.width || 'auto'}px;
      height: ${pos.height || 'auto'}px;
      ${pos.rotation ? `transform: rotate(${pos.rotation}deg);` : ''}
      ${element.opacity !== undefined ? `opacity: ${element.opacity};` : ''}
    `.trim();

    switch (element.type) {
      case 'TEXT': {
        const style = element.style || {};
        const textStyle = `
          font-family: ${style.fontFamily || 'Georgia, serif'};
          font-size: ${style.fontSize || 16}px;
          font-weight: ${style.fontWeight || 'normal'};
          font-style: ${style.fontStyle || 'normal'};
          text-decoration: ${style.textDecoration || 'none'};
          text-align: ${style.textAlign || 'center'};
          line-height: ${style.lineHeight || 1.4};
          letter-spacing: ${style.letterSpacing || 0}px;
          color: ${style.color || '#111827'};
          display: flex;
          align-items: center;
          justify-content: ${style.textAlign === 'center' ? 'center' : style.textAlign === 'right' ? 'flex-end' : 'flex-start'};
        `.trim();
        return `<div style="${baseStyle} ${textStyle}">${element.content || ''}</div>`;
      }

      case 'VARIABLE': {
        const style = element.style || {};
        const variableKey = element.variableKey || 'variable';
        const placeholder = element.placeholder || variableKey;
        const displayValue = sampleData?.[variableKey] || `{{${variableKey}}}`;
        const textStyle = `
          font-family: ${style.fontFamily || 'Georgia, serif'};
          font-size: ${style.fontSize || 16}px;
          font-weight: ${style.fontWeight || 'normal'};
          font-style: ${style.fontStyle || 'normal'};
          text-decoration: ${style.textDecoration || 'none'};
          text-align: ${style.textAlign || 'center'};
          line-height: ${style.lineHeight || 1.4};
          letter-spacing: ${style.letterSpacing || 0}px;
          color: ${style.color || '#111827'};
          display: flex;
          align-items: center;
          justify-content: ${style.textAlign === 'center' ? 'center' : style.textAlign === 'right' ? 'flex-end' : 'flex-start'};
        `.trim();
        // Use {{variable}} syntax for backend processing
        return `<div style="${baseStyle} ${textStyle}">{{${variableKey}}}</div>`;
      }

      case 'SHAPE': {
        const shapeStyle = `
          background-color: ${element.fill || '#E5E7EB'};
          ${element.shapeType === 'circle' ? 'border-radius: 50%;' : ''}
          ${element.border?.radius ? `border-radius: ${element.border.radius}px;` : ''}
          ${element.border?.width ? `border: ${element.border.width}px ${element.border.style || 'solid'} ${element.border.color || '#000'};` : ''}
        `.trim();
        return `<div style="${baseStyle} ${shapeStyle}"></div>`;
      }

      case 'LINE': {
        const lineStyle = `
          background-color: ${element.strokeColor || '#000000'};
          height: ${element.strokeWidth || 2}px;
          ${element.strokeStyle === 'dashed' ? 'background: repeating-linear-gradient(90deg, ' + (element.strokeColor || '#000') + ' 0, ' + (element.strokeColor || '#000') + ' 5px, transparent 5px, transparent 10px);' : ''}
        `.trim();
        return `<div style="${baseStyle} ${lineStyle}"></div>`;
      }

      case 'IMAGE': {
        const imgStyle = `
          object-fit: ${element.fit || 'contain'};
          width: 100%;
          height: 100%;
        `.trim();
        const src = element.src || '';
        return `<div style="${baseStyle}"><img src="${src}" alt="${element.alt || ''}" style="${imgStyle}" /></div>`;
      }

      case 'SIGNATURE': {
        const style = element.style || {};
        if (element.signatureType === 'text') {
          const sigStyle = `
            font-family: ${style.fontFamily || '"Dancing Script", cursive'};
            font-size: ${style.fontSize || 24}px;
            color: ${style.color || '#111827'};
            text-align: center;
          `.trim();
          return `<div style="${baseStyle} ${sigStyle}">
            <div>${element.text || ''}</div>
            ${element.showLabel && element.label ? `<div style="font-size: 12px; color: #6B7280; margin-top: 4px;">${element.label}</div>` : ''}
          </div>`;
        } else {
          return `<div style="${baseStyle}"><img src="${element.src || ''}" alt="Signature" style="max-width: 100%; max-height: 100%;" /></div>`;
        }
      }

      case 'QR_CODE': {
        // QR codes need to be generated server-side or via JS library
        // Include placeholder with data-key for backend processing
        return `<div style="${baseStyle}" data-qr-code="{{${element.dataKey || 'verification_url'}}}" data-size="${element.size || 80}"></div>`;
      }

      default:
        return '';
    }
  }

  /**
   * Parse HTML template to extract basic metadata for preview
   */
  parseHtmlTemplateMetadata(htmlTemplate: string): {
    title?: string;
    badgeText?: string;
    badgeColor?: string;
  } {
    try {
      const titleMatch = htmlTemplate.match(/class="title[^"]*"[^>]*>([^<]+)</i) ||
                        htmlTemplate.match(/Certificate of [A-Za-z]+/i);
      const badgeMatch = htmlTemplate.match(/class="badge[^"]*"[^>]*style="[^"]*background[^:]*:\s*([^;]+)[^"]*"[^>]*>([^<]+)</i);
      return {
        title: titleMatch ? titleMatch[1] || titleMatch[0] : undefined,
        badgeText: badgeMatch ? badgeMatch[2] : undefined,
        badgeColor: badgeMatch ? badgeMatch[1] : undefined
      };
    } catch {
      return {};
    }
  }

  /**
   * Parse HTML template and convert to canvas layout for editing
   * Handles both inline styles AND CSS class-based templates
   */
  parseHtmlToCanvasLayout(htmlTemplate: string): any {
    try {
      const canvasWidth = 842;
      const canvasHeight = 595;
      const elements: any[] = [];
      let elementId = 1;
      const genId = () => `el-${elementId++}-${Date.now()}`;

      // Helper to parse CSS rules from style tag
      const parseCssRules = (html: string): Record<string, Record<string, string>> => {
        const rules: Record<string, Record<string, string>> = {};
        const styleMatch = html.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
        if (!styleMatch) return rules;

        const cssText = styleMatch[1];
        const ruleRegex = /\.([a-zA-Z0-9_-]+)\s*\{([^}]+)\}/g;
        let ruleMatch;

        while ((ruleMatch = ruleRegex.exec(cssText)) !== null) {
          const className = ruleMatch[1];
          const propsStr = ruleMatch[2];
          const props: Record<string, string> = {};

          propsStr.split(';').forEach(part => {
            const colonIdx = part.indexOf(':');
            if (colonIdx > 0) {
              const key = part.substring(0, colonIdx).trim();
              const value = part.substring(colonIdx + 1).trim();
              if (key && value) props[key] = value;
            }
          });

          rules[className] = props;
        }

        return rules;
      };

      // Helper to parse inline style string
      const parseInlineStyle = (styleStr: string): Record<string, string> => {
        const style: Record<string, string> = {};
        if (!styleStr) return style;
        styleStr.split(';').forEach(part => {
          const colonIdx = part.indexOf(':');
          if (colonIdx > 0) {
            const key = part.substring(0, colonIdx).trim();
            const value = part.substring(colonIdx + 1).trim();
            if (key && value) style[key] = value;
          }
        });
        return style;
      };

      // Helper to extract number from px value
      const parsePx = (value: string | undefined): number => {
        if (!value) return 0;
        return parseInt(value.replace('px', '')) || 0;
      };

      // Parse CSS rules from style tag
      const cssRules = parseCssRules(htmlTemplate);
      const hasCssClasses = Object.keys(cssRules).length > 0;

      // Detect template type: CSS classes vs inline styles
      const hasInlinePositioning = htmlTemplate.includes('position:absolute') && htmlTemplate.includes('style="');

      if (hasCssClasses && !hasInlinePositioning) {
        // CSS CLASS-BASED TEMPLATE (Gold, Silver, Bronze, Learning Path)
        // These use flexbox layout, so we create elements with calculated positions

        let yOffset = 40; // Starting Y position
        const centerX = canvasWidth / 2;
        let zIndex = 1;

        // Get certificate and border colors from CSS
        const outerBorderCss = cssRules['outer-border'] || {};
        const innerBorderCss = cssRules['inner-border'] || {};
        const cornerCss = cssRules['corner'] || {};

        // Extract border color
        const borderColorMatch = (outerBorderCss['border'] || innerBorderCss['border'] || '').match(/#[0-9a-fA-F]+/);
        const borderColor = borderColorMatch ? borderColorMatch[0] : '#D97706';

        // Parse outer border
        if (outerBorderCss['border']) {
          const top = parsePx(outerBorderCss['top']) || 25;
          const left = parsePx(outerBorderCss['left']) || 35;
          const right = parsePx(outerBorderCss['right']) || 35;
          const bottom = parsePx(outerBorderCss['bottom']) || 25;
          const borderParts = outerBorderCss['border'].match(/(\d+)px\s+(\w+)\s+(#[0-9a-fA-F]+)/);

          elements.push({
            id: genId(),
            type: 'SHAPE',
            name: 'Outer Border',
            shapeType: 'rectangle',
            fill: 'transparent',
            border: {
              width: borderParts ? parseInt(borderParts[1]) : 1,
              style: borderParts ? borderParts[2] : 'solid',
              color: borderParts ? borderParts[3] : borderColor,
              radius: 0
            },
            position: { x: left, y: top, width: canvasWidth - left - right, height: canvasHeight - top - bottom, zIndex: zIndex++ },
            visible: true, locked: false, opacity: 1
          });
        }

        // Parse inner border
        if (innerBorderCss['border']) {
          const top = parsePx(innerBorderCss['top']) || 35;
          const left = parsePx(innerBorderCss['left']) || 45;
          const right = parsePx(innerBorderCss['right']) || 45;
          const bottom = parsePx(innerBorderCss['bottom']) || 35;
          const borderParts = innerBorderCss['border'].match(/(\d+)px\s+(\w+)\s+(#[0-9a-fA-F]+)/);

          elements.push({
            id: genId(),
            type: 'SHAPE',
            name: 'Inner Border',
            shapeType: 'rectangle',
            fill: 'transparent',
            border: {
              width: borderParts ? parseInt(borderParts[1]) : 2,
              style: borderParts ? borderParts[2] : 'solid',
              color: borderParts ? borderParts[3] : borderColor,
              radius: 0
            },
            position: { x: left, y: top, width: canvasWidth - left - right, height: canvasHeight - top - bottom, zIndex: zIndex++ },
            visible: true, locked: false, opacity: 1
          });
        }

        // Parse corners (L-shaped brackets)
        const cornerColorMatch = (cornerCss['border-color'] || '').match(/#[0-9a-fA-F]+/);
        const cornerColor = cornerColorMatch ? cornerColorMatch[0] : borderColor;
        const cornerWidth = parsePx(cssRules['corner']?.['width']) || 15;
        const cornerHeight = parsePx(cssRules['corner']?.['height']) || 15;

        // Get corner positions from CSS
        const corners = [
          { name: 'Top-Left', css: cssRules['corner-tl'], hasLeft: true, hasTop: true },
          { name: 'Top-Right', css: cssRules['corner-tr'], hasRight: true, hasTop: true },
          { name: 'Bottom-Left', css: cssRules['corner-bl'], hasLeft: true, hasBottom: true },
          { name: 'Bottom-Right', css: cssRules['corner-br'], hasRight: true, hasBottom: true }
        ];

        corners.forEach(corner => {
          if (!corner.css) return;

          let x = 0, y = 0;
          if (corner.css['left']) x = parsePx(corner.css['left']);
          if (corner.css['right']) x = canvasWidth - parsePx(corner.css['right']) - cornerWidth;
          if (corner.css['top']) y = parsePx(corner.css['top']);
          if (corner.css['bottom']) y = canvasHeight - parsePx(corner.css['bottom']) - cornerHeight;

          // Vertical line
          elements.push({
            id: genId(),
            type: 'LINE',
            name: `${corner.name} Corner V`,
            strokeWidth: 2,
            strokeColor: cornerColor,
            strokeStyle: 'solid',
            startPoint: { x: 0, y: 0 },
            endPoint: { x: 0, y: cornerHeight },
            position: { x: corner.hasRight ? x + cornerWidth - 2 : x, y, width: 2, height: cornerHeight, zIndex: 10 },
            visible: true, locked: false, opacity: 1
          });

          // Horizontal line
          elements.push({
            id: genId(),
            type: 'LINE',
            name: `${corner.name} Corner H`,
            strokeWidth: 2,
            strokeColor: cornerColor,
            strokeStyle: 'solid',
            startPoint: { x: 0, y: 0 },
            endPoint: { x: cornerWidth, y: 0 },
            position: { x, y: corner.hasBottom ? y + cornerHeight - 2 : y, width: cornerWidth, height: 2, zIndex: 10 },
            visible: true, locked: false, opacity: 1
          });
        });

        // Parse content elements from HTML body
        // Look for elements with class names and extract their content
        const contentElements = [
          { class: 'logo', type: 'IMAGE' },
          { class: 'company-name', type: 'TEXT' },
          { class: 'title', type: 'TEXT' },
          { class: 'title-line', type: 'LINE' },
          { class: 'badge', type: 'TEXT' },
          { class: 'intro-text', type: 'TEXT' },
          { class: 'holder-name', type: 'VARIABLE', varKey: 'holder_name' },
          { class: 'completion-text', type: 'TEXT' },
          { class: 'cert-title', type: 'VARIABLE', varKey: 'certification_name' },
          { class: 'path-title', type: 'VARIABLE', varKey: 'learning_path_title' },
          { class: 'course-title', type: 'VARIABLE', varKey: 'course_title' },
          { class: 'score', type: 'TEXT' },
          { class: 'stats', type: 'TEXT' },
          { class: 'divider', type: 'LINE' },
          { class: 'footer-label', type: 'TEXT' },
          { class: 'footer-value', type: 'VARIABLE' }
        ];

        contentElements.forEach(elDef => {
          const css = cssRules[elDef.class];
          if (!css) return;

          // Find element in HTML
          const elRegex = new RegExp(`<[^>]+class="[^"]*\\b${elDef.class}\\b[^"]*"[^>]*>([\\s\\S]*?)</`, 'gi');
          let elMatch;

          while ((elMatch = elRegex.exec(htmlTemplate)) !== null) {
            let content = elMatch[1].trim();
            // Remove nested HTML tags for content
            content = content.replace(/<[^>]+>/g, '').trim();

            if (!content && elDef.type !== 'LINE' && elDef.type !== 'IMAGE') continue;

            const fontSize = parsePx(css['font-size']) || 14;
            const fontWeight = css['font-weight'] || 'normal';
            const color = css['color'] || '#1E293B';
            const bgColor = css['background'] || css['background-color'];
            const marginBottom = parsePx(css['margin-bottom']) || 10;
            const width = parsePx(css['width']) || 600;
            const height = parsePx(css['height']) || (fontSize + 10);

            const textStyle = {
              fontFamily: css['font-family'] || 'Georgia, serif',
              fontSize,
              fontWeight,
              fontStyle: css['font-style'] || 'normal',
              textAlign: 'center' as const,
              color,
              lineHeight: parseFloat(css['line-height'] || '1.4'),
              letterSpacing: parsePx(css['letter-spacing']) || 0,
              textDecoration: 'none'
            };

            // Check if content has variables
            const varMatch = content.match(/\{\{(\w+)\}\}/);

            if (elDef.type === 'LINE' || elDef.class === 'title-line' || elDef.class === 'divider') {
              elements.push({
                id: genId(),
                type: 'LINE',
                name: elDef.class === 'title-line' ? 'Title Line' : 'Divider',
                strokeWidth: parsePx(css['height']) || 2,
                strokeColor: bgColor || borderColor,
                strokeStyle: 'solid',
                startPoint: { x: 0, y: 0 },
                endPoint: { x: width, y: 0 },
                position: { x: centerX - width / 2, y: yOffset, width, height: parsePx(css['height']) || 2, zIndex: zIndex++ },
                visible: true, locked: false, opacity: 1
              });
            } else if (elDef.type === 'IMAGE') {
              const imgSrcMatch = elMatch[0].match(/src="([^"]+)"/);
              elements.push({
                id: genId(),
                type: 'IMAGE',
                name: 'Logo',
                src: imgSrcMatch ? imgSrcMatch[1] : '{{company_logo_url}}',
                alt: 'Company Logo',
                fit: 'contain',
                position: { x: centerX - 80, y: yOffset, width: 160, height: 55, zIndex: zIndex++ },
                visible: true, locked: false, opacity: 1
              });
            } else if (varMatch || elDef.varKey) {
              const variableKey = varMatch ? varMatch[1] : elDef.varKey!;
              elements.push({
                id: genId(),
                type: 'VARIABLE',
                name: variableKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                variableKey,
                placeholder: `{{${variableKey}}}`,
                style: textStyle,
                format: variableKey.includes('date') ? 'date' : 'text',
                position: { x: centerX - width / 2, y: yOffset, width, height: fontSize + 20, zIndex: zIndex++ },
                visible: true, locked: false, opacity: 1
              });
            } else if (elDef.class === 'badge') {
              // Badge element with background
              elements.push({
                id: genId(),
                type: 'TEXT',
                name: 'Badge',
                content,
                style: { ...textStyle, color: css['color'] || '#000' },
                position: { x: centerX - 60, y: yOffset, width: 120, height: 30, zIndex: zIndex++ },
                visible: true, locked: false, opacity: 1,
                background: bgColor
              });
            } else {
              elements.push({
                id: genId(),
                type: 'TEXT',
                name: content.length > 20 ? content.substring(0, 20) + '...' : content,
                content,
                style: textStyle,
                position: { x: centerX - width / 2, y: yOffset, width, height: fontSize + 20, zIndex: zIndex++ },
                visible: true, locked: false, opacity: 1
              });
            }

            yOffset += height + marginBottom;
          }
        });

        // Parse footer elements
        const footerColRegex = /<div[^>]*class="[^"]*footer-col[^"]*"[^>]*>([\s\S]*?)<\/div>\s*<\/div>/gi;
        let footerMatch;
        let footerX = 100;
        const footerY = canvasHeight - 120;
        const footerColWidth = 200;

        while ((footerMatch = footerColRegex.exec(htmlTemplate)) !== null) {
          const footerContent = footerMatch[1];

          // Extract label
          const labelMatch = footerContent.match(/<div[^>]*class="[^"]*footer-label[^"]*"[^>]*>([^<]+)<\/div>/i);
          if (labelMatch) {
            elements.push({
              id: genId(),
              type: 'TEXT',
              name: labelMatch[1],
              content: labelMatch[1],
              style: {
                fontFamily: 'Arial, sans-serif',
                fontSize: 10,
                fontWeight: 'normal',
                fontStyle: 'normal',
                textAlign: 'center' as const,
                color: '#94A3B8',
                lineHeight: 1.2,
                letterSpacing: 0,
                textDecoration: 'none'
              },
              position: { x: footerX, y: footerY, width: footerColWidth, height: 18, zIndex: zIndex++ },
              visible: true, locked: false, opacity: 1
            });
          }

          // Extract value (variable)
          const valueMatch = footerContent.match(/<div[^>]*class="[^"]*footer-value[^"]*"[^>]*>([^<]*(?:\{\{[^}]+\}\})?[^<]*)<\/div>/i);
          if (valueMatch) {
            const content = valueMatch[1].trim();
            const varMatch = content.match(/\{\{(\w+)\}\}/);

            if (varMatch) {
              elements.push({
                id: genId(),
                type: 'VARIABLE',
                name: varMatch[1].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                variableKey: varMatch[1],
                placeholder: `{{${varMatch[1]}}}`,
                style: {
                  fontFamily: 'Georgia, serif',
                  fontSize: 12,
                  fontWeight: '600',
                  fontStyle: 'normal',
                  textAlign: 'center' as const,
                  color: '#334155',
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  textDecoration: 'none'
                },
                format: varMatch[1].includes('date') ? 'date' : 'text',
                position: { x: footerX, y: footerY + 20, width: footerColWidth, height: 22, zIndex: zIndex++ },
                visible: true, locked: false, opacity: 1
              });
            } else {
              elements.push({
                id: genId(),
                type: 'TEXT',
                name: content,
                content,
                style: {
                  fontFamily: 'Georgia, serif',
                  fontSize: 12,
                  fontWeight: '600',
                  fontStyle: 'normal',
                  textAlign: 'center' as const,
                  color: '#334155',
                  lineHeight: 1.2,
                  letterSpacing: 0,
                  textDecoration: 'none'
                },
                position: { x: footerX, y: footerY + 20, width: footerColWidth, height: 22, zIndex: zIndex++ },
                visible: true, locked: false, opacity: 1
              });
            }
          }

          footerX += footerColWidth + 50;
        }

      } else {
        // INLINE STYLE-BASED TEMPLATE (Course Completion)
        // Parse each div with inline styles

        const divRegex = /<div[^>]*style="([^"]*)"[^>]*>([\s\S]*?)<\/div>/gi;
        let match;

        while ((match = divRegex.exec(htmlTemplate)) !== null) {
          const styleStr = match[1];
          const content = match[2].trim();
          const style = parseInlineStyle(styleStr);

          // Skip if not absolutely positioned
          if (style['position'] !== 'absolute') continue;

          const x = parsePx(style['left']);
          const y = parsePx(style['top']);
          const width = parsePx(style['width']);
          const height = parsePx(style['height']);
          const zIndex = parseInt(style['z-index'] || '1') || elements.length + 1;

          // Check if it's a border frame (large empty div with border)
          if (!content && width > 400 && height > 300 && style['border']) {
            const borderParts = style['border'].match(/(\d+)px\s+(\w+)\s+(#[0-9a-fA-F]+|\w+)/);
            if (borderParts) {
              elements.push({
                id: genId(),
                type: 'SHAPE',
                name: 'Border Frame',
                shapeType: 'rectangle',
                fill: 'transparent',
                border: {
                  width: parseInt(borderParts[1]) || 1,
                  style: borderParts[2] || 'solid',
                  color: borderParts[3] || '#1E3A5F',
                  radius: 0
                },
                position: { x, y, width, height, zIndex },
                visible: true, locked: false, opacity: 1
              });
              continue;
            }
          }

          // Check if it's a corner decoration (small div with partial borders)
          if (!content && width <= 20 && height <= 20) {
            const hasBorderLeft = style['border-left'];
            const hasBorderRight = style['border-right'];
            const hasBorderTop = style['border-top'];
            const hasBorderBottom = style['border-bottom'];

            if (hasBorderLeft || hasBorderRight || hasBorderTop || hasBorderBottom) {
              const borderStr = hasBorderLeft || hasBorderRight || hasBorderTop || hasBorderBottom || '';
              const colorMatch = borderStr.match(/#[0-9a-fA-F]+/i);
              const borderColor = colorMatch ? colorMatch[0] : '#1E3A5F';
              const strokeWidth = parseInt(borderStr.match(/(\d+)px/)?.[1] || '2');

              let cornerName = 'Corner';
              if (hasBorderLeft && hasBorderTop) cornerName = 'Top-Left Corner';
              else if (hasBorderRight && hasBorderTop) cornerName = 'Top-Right Corner';
              else if (hasBorderLeft && hasBorderBottom) cornerName = 'Bottom-Left Corner';
              else if (hasBorderRight && hasBorderBottom) cornerName = 'Bottom-Right Corner';

              // Vertical line
              elements.push({
                id: genId(),
                type: 'LINE',
                name: `${cornerName} V`,
                strokeWidth,
                strokeColor: borderColor,
                strokeStyle: 'solid',
                startPoint: { x: 0, y: 0 },
                endPoint: { x: 0, y: height },
                position: { x: hasBorderRight ? x + width - strokeWidth : x, y, width: strokeWidth, height, zIndex },
                visible: true, locked: false, opacity: 1
              });

              // Horizontal line
              elements.push({
                id: genId(),
                type: 'LINE',
                name: `${cornerName} H`,
                strokeWidth,
                strokeColor: borderColor,
                strokeStyle: 'solid',
                startPoint: { x: 0, y: 0 },
                endPoint: { x: width, y: 0 },
                position: { x, y: hasBorderBottom ? y + height - strokeWidth : y, width, height: strokeWidth, zIndex },
                visible: true, locked: false, opacity: 1
              });
              continue;
            }
          }

          // Check if it contains an image
          const imgMatch = content.match(/<img[^>]+src="([^"]+)"[^>]*>/i);
          if (imgMatch) {
            elements.push({
              id: genId(),
              type: 'IMAGE',
              name: 'Logo',
              src: imgMatch[1],
              alt: 'Company Logo',
              fit: 'contain',
              position: { x, y, width, height, zIndex },
              visible: true, locked: false, opacity: 1
            });
            continue;
          }

          // Check if it's a line (thin div with background)
          if (!content && (height <= 5 || width <= 5) && style['background-color']) {
            elements.push({
              id: genId(),
              type: 'LINE',
              name: 'Line',
              strokeWidth: height <= 5 ? height : width,
              strokeColor: style['background-color'],
              strokeStyle: 'solid',
              startPoint: { x: 0, y: 0 },
              endPoint: { x: width, y: 0 },
              position: { x, y, width, height: Math.max(height, 2), zIndex },
              visible: true, locked: false, opacity: 1
            });
            continue;
          }

          // Skip empty divs
          if (!content) continue;

          // Extract text styles
          const textStyle = {
            fontFamily: style['font-family'] || 'Georgia, serif',
            fontSize: parsePx(style['font-size']) || 14,
            fontWeight: style['font-weight'] || 'normal',
            fontStyle: style['font-style'] || 'normal',
            textAlign: (style['text-align'] || 'center') as 'left' | 'center' | 'right',
            color: style['color'] || '#111827',
            lineHeight: parseFloat(style['line-height'] || '1.4'),
            letterSpacing: parsePx(style['letter-spacing']) || 0,
            textDecoration: 'none'
          };

          // Check if content is a variable
          const varMatch = content.match(/\{\{(\w+)\}\}/);
          if (varMatch) {
            const variableKey = varMatch[1];
            elements.push({
              id: genId(),
              type: 'VARIABLE',
              name: variableKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              variableKey,
              placeholder: `{{${variableKey}}}`,
              style: textStyle,
              format: variableKey.includes('date') ? 'date' : 'text',
              position: { x, y, width, height, zIndex },
              visible: true, locked: false, opacity: 1
            });
          } else {
            const cleanContent = content.replace(/<[^>]+>/g, '').trim();
            if (cleanContent) {
              elements.push({
                id: genId(),
                type: 'TEXT',
                name: cleanContent.length > 20 ? cleanContent.substring(0, 20) + '...' : cleanContent,
                content: cleanContent,
                style: textStyle,
                position: { x, y, width, height, zIndex },
                visible: true, locked: false, opacity: 1
              });
            }
          }
        }
      }

      // Sort elements by zIndex
      elements.sort((a, b) => (a.position?.zIndex || 0) - (b.position?.zIndex || 0));

      return {
        canvas: {
          width: canvasWidth,
          height: canvasHeight,
          background: { type: 'solid', color: '#FFFFFF' },
          border: null
        },
        elements: elements.length > 0 ? elements : undefined
      };
    } catch (error) {
      console.error('Error parsing HTML to canvas:', error);
      return null;
    }
  }
}

export const certificateTemplateService = new CertificateTemplateService();
export default certificateTemplateService;
